import React, { useCallback, useEffect, useRef } from 'react'
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'
import { Divider } from '@ui-kitten/components'
import { omit } from 'lodash'
import {
  PlatformPay,
  createPlatformPayPaymentMethod,
  confirmSetupIntent,
  updatePlatformPaySheet,
  buildCartItems,
} from '@src/components/PlatformPay'
import { StyleService, useStyleSheet } from '@src/style/service'
import { isProductionEnvironment } from '@src/config/apollo'
import { CheckoutContainer } from '@src/screens/Marketplace/components/checkout/CheckoutContainer'
import { MicrobiomeOutOfStockNotice } from '@src/screens/Marketplace/components/checkout/MicrobiomeOutOfStockNotice'
import { ProductDescription } from '@src/screens/Marketplace/components/checkout/ProductDescription'
import { AddressForm, AddressType } from '@src/screens/Marketplace/components/checkout/AddressForm'
import { OnboardingStackParamsList } from '@src/navigation/types'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT } from '@config/momentFormat'
import { InlineAlert } from '@components'
import {
  useEstimateTax,
  usePromotionCode,
  usePurchaseSubscription,
  useTaxEstimation,
  useUpgradeSubscriptionProduct,
  useUpgradeSubscriptionTrial,
} from '@screens/Marketplace/utils/hooks'
import {
  AddressCountry,
  AddressState,
  BillingProduct,
  Maybe,
  SubscriptionCheckoutOptionKind,
  SurveysConfigKind,
  UiInlineAlert,
  UiStateNames,
} from '@src/types'
import { useSnack } from '@src/utils/navigatorContext'
import { userSelector } from '@src/selectors/app'
import { useDispatchAsync } from '@src/utils'
import { useShouldShowPurchaseFlow } from '@src/utils/hooks'
import { useShouldShowByosOnboardingTutorials } from '@src/screens/OwnSensorOnboarding/hooks'
import { Analytics, Apollo, CustomEventTypes } from '@src/config'
import { StartUnifiedOnboardingMutation } from '@src/models/unifiedOnboarding/graphql/startUnifiedOnboarding.generated'
import { useSecureScreen } from '@src/hooks/useSecureScreen'
import { CheckoutKind } from '../../types/types'
import { calculateDiscount, getPriceDescription } from '../../utils/utils'
import { CreateProviderSetupIntentMutation } from '../../graphql/createProviderSetupIntent.generated'
import { DiscountOffer } from './DiscountOffer'
import { OrderSummary } from './OrderSummary'
import { PaymentMethodCard } from './PaymentMethodCard'
import { CheckoutAddress, getMissingCheckoutAddressFields } from './addressValidation'

export const SubscriptionCheckout = () => {
  useSecureScreen()

  const styles = useStyleSheet(themedStyle)

  const navigation = useNavigation()
  const route = useRoute<RouteProp<OnboardingStackParamsList, 'SubscriptionCheckout'>>()
  const {
    product,
    subscriptionId,
    checkoutKind,
    checkoutOptionKinds,
    savings,
    trialEndsAt,
    showMicrobiomeTest,
    nextScreen,
  } = route.params

  useLazyScreenPreload('SteloPaymentSuccessful', {
    shouldPreload: !!checkoutOptionKinds?.includes(SubscriptionCheckoutOptionKind.Stelo),
  })

  const purchaseSubscription = usePurchaseSubscription()
  const upgradeSubscriptionTrial = useUpgradeSubscriptionTrial()
  const upgradeSubscriptionProduct = useUpgradeSubscriptionProduct()
  const dispatch = useDispatch()
  const dispatchAsync = useDispatchAsync()
  const showSnack = useSnack()
  const shouldShowPurchaseFlow = useShouldShowPurchaseFlow()
  const shouldShowOnboardingTutorials = useShouldShowByosOnboardingTutorials()
  const onboardingIsInProgress = shouldShowPurchaseFlow || shouldShowOnboardingTutorials

  const productKey = product.key as BillingProduct

  const promotionCode = usePromotionCode(productKey, product.appliedPromo)

  const user = useSelector(userSelector)
  const address = user?.address
  const { estimatedTax } = useTaxEstimation({
    productKey,
    promotionCode,
    zipCode: address?.zipCode,
    country: address?.country,
  })

  const estimateTax = useEstimateTax()

  const paymentMethod = user?.paymentMethod

  const { requiresShipping, trialDays } = product

  const addressType = requiresShipping ? AddressType.Shipping : AddressType.Billing

  const navigateToAddressScreen = useCallback(() => {
    navigation.navigate('ChangeAddress', { addressType })
  }, [addressType, navigation])

  const executedRef = useRef(false)

  const shouldNavigateToAddressScreen = !!address && !user.phoneNumber

  useEffect(() => {
    // execute it only once on mount, otherwise we would navigate again to address modal
    // if user adds the address and phone number for the first time
    if (executedRef.current) {
      return
    }

    executedRef.current = true

    if (shouldNavigateToAddressScreen) {
      navigateToAddressScreen()
    }
  }, [navigateToAddressScreen, shouldNavigateToAddressScreen])

  const allowedCountries = Object.values(AddressCountry)

  const validCheckoutAddress = (address?: CheckoutAddress): address is CheckoutAddress => {
    const missingFields = getMissingCheckoutAddressFields(address)

    if (!missingFields.length) {
      return true
    }

    showSnack(
      `Please add a complete ${addressType.toLowerCase()} address to continue`,
      null,
      'error',
    )
    navigateToAddressScreen()
    return false
  }

  const createSubscription = async ({
    paymentMethodId,
    address,
  }: {
    paymentMethodId?: string
    address: CheckoutAddress
  }) => {
    if (address.country && !allowedCountries.includes(address.country as AddressCountry)) {
      showSnack(`We currently only ship to ${allowedCountries.join(', ')}`, null, 'error')
      return
    }

    const subscription = await purchaseSubscription({
      productKey: product.key,
      paymentMethodId,
      checkoutOptionKinds,
      promotionCode,
      address,
    })

    if (!subscription) {
      return
    }

    let showUnifiedOnboarding = false

    if (!nextScreen) {
      try {
        const startUnifiedOnboardingResponse = await dispatchAsync<
          StartUnifiedOnboardingMutation['startUnifiedOnboarding']
        >({
          type: 'unifiedOnboarding/start',
        })

        showUnifiedOnboarding = startUnifiedOnboardingResponse.success

        if (showUnifiedOnboarding) {
          dispatch({
            type: 'uiFlags/updateUiFlags',
            payload: { showUnifiedOnboarding: true },
          })
        }
      } catch {
        // do nothing
      }
    }

    const showSteloPaymentSuccessfulScreen =
      checkoutOptionKinds?.includes(SubscriptionCheckoutOptionKind.Stelo) && !showUnifiedOnboarding

    if (showSteloPaymentSuccessfulScreen) {
      navigation.reset({
        index: 0,
        routes: [
          {
            name: 'SteloPaymentSuccessful',
          },
        ],
      })
    }

    if (showUnifiedOnboarding) {
      navigation.reset({
        index: 0,
        routes: [
          {
            name: 'UnifiedOnboardingStart',
          },
        ],
      })
    }

    dispatch({ type: 'uiFlags/fetch' })

    try {
      await dispatchAsync({ type: 'users/fetch' })
    } catch {
      // do nothing
    }

    if (nextScreen) {
      navigation.reset({
        index: 0,
        routes: [{ name: nextScreen.screen, params: nextScreen.params }],
      })
      return
    }

    if (!onboardingIsInProgress) {
      if (!showSteloPaymentSuccessfulScreen) {
        navigation.reset({
          index: 2,
          routes: [
            {
              name: 'Drawer',
              state: { routes: [{ name: 'Dashboard' }] },
            },
            { name: 'Account' },
            {
              name: 'Subscriptions',
            },
          ],
        })
      }
      return
    }

    dispatch({
      type: 'app/upsertUiState',
      payload: {
        name: UiStateNames.CheckoutCompleted,
        value: true,
      },
    })
    if (subscription.primaryProduct.ownSensor && !showUnifiedOnboarding) {
      navigation.navigate('GoalQuestionnaire', {
        questionnaire: SurveysConfigKind.OwnSensorGoal,
        nextScreen: { screen: 'ConfirmSensorSelection' },
      })
    }
    return
  }

  const onSignUpPress = async () => {
    if (!subscriptionId) {
      const checkoutAddress = address ? omit(address, ['__typename']) : undefined

      if (!validCheckoutAddress(checkoutAddress)) {
        return
      }

      await createSubscription({
        paymentMethodId: paymentMethod?.stripeId,
        address: checkoutAddress,
      })
      return
    }

    if (checkoutKind === CheckoutKind.UpgradeTrial) {
      await upgradeSubscriptionTrial(subscriptionId, promotionCode)
    } else if (checkoutKind === CheckoutKind.UpgradeProduct) {
      await upgradeSubscriptionProduct(subscriptionId, product, checkoutOptionKinds, promotionCode)
    }
  }

  const discount = calculateDiscount(product.price, promotionCode)

  const discountedPrice = product.price - discount

  const amount = Math.max(discountedPrice + estimatedTax, 0)

  const onPlatformPayButtonPress = async () => {
    Analytics.track(CustomEventTypes.PlatformPayStarted)

    const { fullName, phoneNumber, email } = user || {}
    let clientSecret: string

    try {
      const { createProviderSetupIntent } = await dispatchAsync<CreateProviderSetupIntentMutation>({
        type: 'marketplace/createProviderSetupIntent',
        payload: {
          email,
          fullName,
          phoneNumber,
        },
      })
      clientSecret = createProviderSetupIntent.clientSecret
    } catch {
      return
    }

    const cartItems = buildCartItems({
      productTitle: product.title,
      productPrice: product.price,
      discount,
      tax: estimatedTax,
      total: amount,
    })

    const {
      error: platformPayError,
      paymentMethod,
      shippingContact,
    } = await createPlatformPayPaymentMethod({
      googlePay: {
        amount: amount * 100, // in cents
        testEnv: !isProductionEnvironment(Apollo.endpointEnv),
        merchantName: 'Nutrisense, Inc.',
        merchantCountryCode: 'US',
        currencyCode: 'USD',
      },
      applePay: {
        requiredShippingAddressFields: requiresShipping
          ? [PlatformPay.ContactField.PostalAddress]
          : undefined,
        requiredBillingContactFields:
          requiresShipping || address ? undefined : [PlatformPay.ContactField.PostalAddress],
        cartItems,
        merchantCountryCode: 'US',
        currencyCode: 'USD',
      },
    })

    if (platformPayError) {
      console.log(platformPayError.code, platformPayError.message)
      Analytics.track(CustomEventTypes.PlatformPayFailed, {
        code: platformPayError.code,
        message: platformPayError.message,
      })
      return
    }

    const { error: setupIntentError, setupIntent } = await confirmSetupIntent(clientSecret, {
      paymentMethodType: 'Card',
      paymentMethodData: { paymentMethodId: paymentMethod.id },
    })

    if (setupIntentError) {
      console.log(setupIntentError.code, setupIntentError.message)
      return
    }

    Analytics.track(CustomEventTypes.PlatformPayCompleted)

    let shippingAddress = user?.address ? omit(user.address, ['__typename']) : undefined

    if (shippingContact?.postalAddress) {
      const [street, street2] = (shippingContact.postalAddress.street || '').split('\n')
      shippingAddress = {
        name:
          `${shippingContact.name.givenName} ${shippingContact.name.familyName}`.trim() ||
          fullName ||
          '',
        street: street || '',
        street2: street2 || '',
        city: shippingContact.postalAddress.city || '',
        state: shippingContact.postalAddress.state as Maybe<AddressState>,
        country: shippingContact.postalAddress.isoCountryCode,
        zipCode: shippingContact.postalAddress.postalCode || '',
      }
    }

    if (!shippingAddress) {
      shippingAddress = {
        name: paymentMethod.billingDetails.name || fullName || '',
        street: paymentMethod.billingDetails.address?.line1 || '',
        street2: paymentMethod.billingDetails.address?.line2 || '',
        city: paymentMethod.billingDetails.address?.city || '',
        state: paymentMethod.billingDetails.address?.state as Maybe<AddressState>,
        country: paymentMethod.billingDetails.address?.country || '',
        zipCode: paymentMethod.billingDetails.address?.postalCode || '',
      }
    }

    if (!validCheckoutAddress(shippingAddress)) {
      return
    }

    await createSubscription({
      paymentMethodId: setupIntent.paymentMethod?.id,
      address: shippingAddress,
    })

    if (paymentMethod.Card.expMonth && paymentMethod.Card.expYear) {
      dispatch({
        type: 'app/updateUserState',
        payload: {
          paymentMethod: {
            stripeId: paymentMethod.id,
            cardType: paymentMethod.Card.brand,
            last4: paymentMethod.Card.last4,
            expirationDate: new Date(
              paymentMethod.Card.expYear,
              paymentMethod.Card.expMonth - 1,
              1,
            ),
          },
        },
      })
    }
  }

  const onEditPromotionCode = () => {
    navigation.navigate('DiscountCode', { product: productKey })
  }

  const onEditPaymentMethodPress = () => {
    navigation.navigate('CreatePaymentMethod')
  }

  const chargeDate = trialDays
    ? moment().add(trialDays, 'days').format('MM/DD/YYYY')
    : moment().format('MM/DD/YYYY')

  return (
    <CheckoutContainer
      checkoutButtonTitle="Check Out"
      showDiscountDisclosure={!!savings && savings > 0}
      onCheckout={onSignUpPress}
      // show platform pay button only in new subscription flow
      onPlatformPayButtonPress={!subscriptionId ? onPlatformPayButtonPress : undefined}
      platformPayButtonType={PlatformPay.ButtonType.Checkout}
      onPlatformPayShippingContactSelected={async (event) => {
        const { shippingContact } = event

        let newEstimatedTax = estimatedTax

        const zipCode = shippingContact.postalAddress.postalCode
        const country = shippingContact.postalAddress.isoCountryCode

        if (zipCode && country) {
          try {
            newEstimatedTax = await estimateTax({
              productKey,
              providerPromotionCodeId: promotionCode?.providerId,
              zipCode,
              country,
            })
          } catch {
            // do nothing
          }
        }

        const newAmount = Math.max(discountedPrice + newEstimatedTax, 0)

        const cartItems = buildCartItems({
          productTitle: product.title,
          productPrice: product.price,
          discount,
          tax: newEstimatedTax,
          total: newAmount,
        })

        await updatePlatformPaySheet({
          applePay: {
            cartItems,
            shippingMethods: [],
            errors: [],
          },
        })
      }}
    >
      <ProductDescription
        title={product.title}
        description={product.interval.description}
        price={getPriceDescription(product.price, product.interval)}
        showDivider={false}
      />
      {showMicrobiomeTest && <MicrobiomeOutOfStockNotice />}
      <Divider />
      <DiscountOffer code={promotionCode} onEditPromotionCode={onEditPromotionCode} />
      <Divider appearance="large" />
      <OrderSummary
        price={product.price}
        discount={discount}
        estimatedTax={discountedPrice > 0 ? estimatedTax : 0}
        chargeDate={chargeDate}
        style={styles.orderSummary}
      />
      <Divider appearance="large" />
      <AddressForm
        addressType={addressType}
        address={address}
        onEditAddressPress={navigateToAddressScreen}
      />
      <Divider appearance="large" />
      <PaymentMethodCard
        paymentMethod={paymentMethod}
        style={styles.paymentMethod}
        onEditPaymentMethodPress={onEditPaymentMethodPress}
      />
      {trialDays && paymentMethod && (
        <InlineAlert
          style={styles.info}
          category={UiInlineAlert.Info}
          message={`Enjoy ${trialDays}-day free trial with no charges`}
          // eslint-disable-next-line max-len
          secondaryMessage="We've got your credit card information, but don't worry, we won't charge you until the trial ends."
        />
      )}
      {trialEndsAt && checkoutKind === CheckoutKind.UpgradeTrial && (
        <InlineAlert
          style={styles.info}
          category={UiInlineAlert.Danger}
          message={`You’ll be billed on ${moment(trialEndsAt).format(
            MONTH_NAME_AND_DATE_WITH_YEAR_FORMAT,
          )}, when your complimentary period ends.`}
        />
      )}
    </CheckoutContainer>
  )
}

const themedStyle = StyleService.create({
  info: {
    margin: 16,
  },
  orderSummary: {
    marginBottom: 16,
    marginHorizontal: 16,
  },
  paymentMethod: {
    paddingHorizontal: 16,
  },
})
